<template>
  <ProgressModal
    v-if="isLoadingAction"
    title="Please wait..."
    :progressIndicator="progressIndicator"
    :processedItems="processedItems"
    :totalItems="exportSelected.length"
  />

  <div
    id="export-table"
    :class="`content-wrapper animation-slide ${
      isMenuOpen ? 'menu-open' : 'menu-closed'
    }`"
  >
    <div class="content content-styles text-white ov-visible md:mx-[1rem]">
      <div class="box text-white large content-box-style ov-hidd p-0">
        <Filters
          :options="options"
          :showActionButton="true"
          :search="search"
          :resetFilter="resetFilter"
          :resetPagination="resetPagination"
        />

        <Transition name="fade-slide">
          <div
            v-if="exportSelected.length > 0"
            class="manage-freebets-status mt-3"
          >
            <div class="flex items-center gap-2">
              <button
                v-for="(btn, index) in exportsBtn"
                :key="index"
                @click="btn.action()"
                :class="[
                  'inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm transition-all duration-200 active:scale-95 shadow-sm whitespace-nowrap',
                  btn.label === 'remove'
                    ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-200'
                    : '',
                  btn.label === 'archive' || btn.label === 'unarchive'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200'
                    : '',
                ]"
              >
                <span>{{ btn.name }}</span>
                <span class="opacity-60 text-xs"
                  >({{ exportSelected.length }})</span
                >
                <i :class="`fas fa-${btn.icon} text-xs`"></i>
              </button>
            </div>
          </div>
        </Transition>

        <div
          v-if="isLoading"
          class="flex-grow flex flex-col items-center justify-center p-20"
        >
          <Loader />
        </div>

        <div
          v-else
          class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3 mt-3"
        >
          <DataTable
            v-model:selection="exportSelected"
            :value="tableData"
            :loading="isLoading"
            :lazy="true"
            :paginator="true"
            :rows="selectedLimit"
            :totalRecords="totalElements"
            :first="(currentPage - 1) * selectedLimit"
            dataKey="_id"
            @page="onPage"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :rowsPerPageOptions="[15, 25, 50, 100]"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} exports"
            showCurrentPageReport
            stripedRows
            scrollable
            scrollHeight="flex"
            class="freebets-datatable p-datatable-sm"
          >
            <template #empty>
              <div class="text-center py-4 text-muted">No exports found.</div>
            </template>
            <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>

            <Column
              v-for="col in columns.filter((c) => c.slug)"
              :key="col.slug"
              :field="col.slug"
              :header="col.label || col.slug"
              :sortable="true"
            >
              <template #body="{ data, field }">
                <div
                  v-if="col.type === 'boolean'"
                  class="flex items-center gap-2"
                >
                  <span
                    :class="[
                      'inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold transition-colors duration-200',
                      data[field]
                        ? 'bg-amber-100 text-amber-700 border border-amber-200'
                        : 'bg-emerald-100 text-emerald-700 border border-emerald-200',
                    ]"
                  >
                    <i
                      :class="[
                        'pi',
                        data[field] ? 'pi-info-circle' : 'pi-check-circle',
                      ]"
                      style="font-size: 0.85rem"
                    ></i>
                    {{ data[field] ? "Archived" : "Active" }}
                  </span>
                </div>

                <div v-else-if="col.type === 'indicator'">
                  <span
                    :class="getStatusClass(data[field])"
                    class="px-3 py-1 rounded-full text-[10px] font-bold uppercase w-fit shadow-sm"
                  >
                    {{ data[field] }}
                  </span>
                </div>

                <span v-else-if="col.type === 'date'">
                  {{ formatDate(data[field]) }}
                </span>

                <span v-else>{{ data[field] }}</span>
              </template>
            </Column>

            <Column
              header="Actions"
              headerStyle="min-width: 8rem"
              :exportable="false"
            >
              <template #body="slotProps">
                <div class="flex items-center gap-1">
                  <Button
                    icon="pi pi-download"
                    rounded
                    :disabled="
                      slotProps.data.status?.toLowerCase() !== 'authorized'
                    "
                    text
                    severity="success"
                    v-tooltip.top="'Download File'"
                    @click="downloadResource(slotProps.data)"
                    class="hover:bg-emerald-50 rounded-full"
                  />
                  <Button
                    icon="pi pi-eye"
                    rounded
                    text
                    severity="info"
                    v-tooltip.top="'View Details'"
                    @click="showExportDetail(slotProps.data)"
                    class="hover:bg-blue-50 rounded-full"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <ExportDetailModal
      v-if="export_detail"
      :downloadResource="downloadResource"
      :exportData="export_detail"
      @close="export_detail = null"
    />
  </Teleport>
</template>

<script>
import Filters from "@/slot/components/table/Filters.vue";
import { mapGetters } from "vuex";
import { formatString } from "@/helpers/date";
import { getFetch, deleteRequest, putFetch } from "@/api/axiosClient";
import { getActiveProviderSlug } from "@/helpers/provider";
import Swal from "sweetalert2";
import ProgressModal from "@/slot/components/Modal/ProgressModal.vue";
import ExportDetailModal from "@/slot/components/Modal/ExportDetailModal.vue";
import Loader from "@/components/Loader.vue";

export default {
  name: "ExportPage",
  components: {
    Filters,
    ProgressModal,
    ExportDetailModal,
    Loader,
  },
  computed: {
    ...mapGetters(["isMenuOpen", "isAdmin"]),
    category() {
      if (this.$route.params.category) return this.$route.params.category;
      if (this.$route.meta?.section) return this.$route.meta.section;
      if (this.$route.path.includes("/livecasino")) return "livecasino";
      if (this.$route.path.includes("/instant")) return "instant";
      return "slot";
    },
    currentDb() {
      const provider = getActiveProviderSlug ? getActiveProviderSlug() : "theearcasino";
      return `${provider}_${this.category}`;
    },
  },
  data() {
    return {
      exportsBtn: [
        {
          name: "Remove",
          label: "remove",
          action: () => this.removeExport(),
          icon: "trash",
        },
        {
          name: "Archive",
          label: "archive",
          action: () => this.archiveExport("archive"),
          icon: "inbox",
        },
        {
          name: "Unarchive",
          label: "unarchive",
          action: () => this.archiveExport("unarchive"),
          icon: "folder-open",
        },
      ],
      options: [
        {
          label: "Start Date",
          type: "datetime-local",
          property: "start_date",
          isDateTime: true,
        },
        {
          label: "End Date",
          type: "datetime-local",
          property: "end_date",
          isDateTime: true,
        },
        {
          label: "Status",
          type: "select",
          property: "export_status",
          options: ["All", "Pending", "Authorized", "Rejected"],
        },
        {
          label: "Is Archived",
          type: "select",
          property: "is_archived",
          options: ["All", "True", "False"],
        },
        {
          label: "File Type",
          type: "select",
          property: "export_file_format",
          options: ["All", "CSV"],
        },
      ],
      columns: [
        { slug: "title", label: "Title", type: "text" },
        { slug: "model", label: "Resource", type: "text" },
        { slug: "type", label: "Type", type: "text" },
        { slug: "is_archived", label: "Archived", type: "boolean" },
        { slug: "status", label: "Status", type: "indicator" },
        { slug: "created_at", label: "Created At", type: "date" },
      ],
      // State
      tableData: [],
      exportSelected: [],
      isLoading: false,
      isLoadingAction: false,
      currentPage: 1,
      selectedLimit: 15,
      totalElements: 0,
      processedItems: 0,
      progressIndicator: 0,
      export_detail: null,
      start_date: null,
      end_date: null,
      export_status: "All",
      export_file_format: "All",
      is_archived: "All",
    };
  },
  computed: {
    skinFilt() {
      const param = this.$route.params.skinFilt;
      if (param && param !== "all") return param;
      return this.$store.getters.skinStorage || "";
    },
  },
  methods: {
    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search();
    },
    formatDate(date) {
      if (!date) return "-";
      return (
        new Date(date).toLocaleDateString() +
        " " +
        new Date(date).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    },
    getStatusClass(status) {
      const s = status?.toLowerCase();
      if (s === "authorized") return "bg-emerald-100 text-emerald-700";
      if (s === "pending") return "bg-amber-100 text-amber-700";
      if (s === "rejected") return "bg-rose-100 text-rose-700";
      return "bg-gray-100 text-gray-700";
    },
    showExportDetail(row) {
      this.export_detail = row;
    },
    async resetFilter() {
      this.currentPage = 1;
      this.selectedLimit = 15;
      this.start_date = this.getInitialTime("start");
      this.end_date = null;
      this.export_file_format = "All";
      this.export_status = "All";
      this.is_archived = "All";
      await this.search();
    },
    getInitialTime(type = "start") {
      const now = new Date();
      if (type === "start") {
        const startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 3);
        startDate.setHours(0, 0, 0, 0);
        return formatString(startDate.toString());
      }
      return formatString(now.toString());
    },
    async downloadResource(row) {
      const url =
        "https://storage.googleapis.com/stage-camasy-images/" + row.path;
      const link = document.createElement("a");
      link.href = url;
      link.download = row.path.split("/").pop();
      document.body.appendChild(link);
      console.log("link", link);
      link.click();
      document.body.removeChild(link);
    },
    async archiveExport(action) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: `This will ${action} the selected exports.`,
        showCancelButton: true,
        confirmButtonText: "Yes, proceed",
      });

      if (!result.isConfirmed) return;

      this.isLoadingAction = true;
      const total = this.exportSelected.length;

      try {
        for (let i = 0; i < total; i++) {
          const exportItem = this.exportSelected[i];
          const newStatus = action === "archive";
          await putFetch(
            "exports/archive",
            {
              _id: exportItem._id,
              archive: newStatus,
            },
            this.currentDb
          );
          this.processedItems = i + 1;
          this.progressIndicator = Math.round(((i + 1) / total) * 100);
        }
        await this.search();
        this.exportSelected = [];
        Swal.fire("Success", "Exports updated successfully", "success");
      } catch (e) {
        Swal.fire("Error", "Failed to update exports", "error");
      } finally {
        this.isLoadingAction = false;
      }
    },
    async removeExport() {
      const pendingExports = this.exportSelected.filter((item) =>
        ["pending", "rejected"].includes(item.status.toLowerCase()),
      );

      if (pendingExports.length === 0) {
        Swal.fire(
          "Attention!",
          "Only 'Pending' or 'Rejected' exports can be removed.",
          "info",
        );
        return;
      }

      const result = await Swal.fire({
        title: "Delete exports?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
      });

      if (!result.isConfirmed) return;

      this.isLoadingAction = true;
      try {
        for (let i = 0; i < pendingExports.length; i++) {
          await deleteRequest("exports", { _id: pendingExports[i]._id }, this.currentDb);
          this.processedItems = i + 1;
          this.progressIndicator = Math.round(
            ((i + 1) / pendingExports.length) * 100,
          );
        }
        await this.search();
        this.exportSelected = [];
        Swal.fire("Deleted!", "Selected exports have been removed.", "success");
      } catch (e) {
        Swal.fire("Error", "Delete failed.", "error");
      } finally {
        this.isLoadingAction = false;
      }
    },
    async search() {
      this.isLoading = true;
      try {
        const params = {
          skin_id: this.skinFilt,
          page: this.currentPage,
          limit: this.selectedLimit,
        };

        if (this.start_date)
          params.start_date = new Date(this.start_date + "Z");
        if (this.end_date) params.end_date = new Date(this.end_date + "Z");
        if (this.export_file_format !== "All")
          params.export_file_format = this.export_file_format.toLowerCase();
        if (this.export_status !== "All")
          params.export_status = this.export_status.toLowerCase();
        if (this.is_archived !== "All")
          params.is_archived = this.is_archived.toLowerCase() === "true";

        const data = await getFetch("exports/", params, true, this.currentDb);
        this.tableData = data.data;
        this.totalElements = data.totalItems;
        this.totalPages = data.totalPages;
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        this.isLoading = false;
      }
    },
    resetPagination() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.resetFilter();
  },
};
</script>

<style scoped></style>
